import classNames from 'classnames'
import React, {
  ChangeEventHandler,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react'
import { isFirefox } from 'react-device-detect'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { Label } from './Label'

export type InputType = 'text' | 'number' | 'email' | 'search' | 'password' | 'tel'

export type RoundedBorders = {
  bottomLeft?: boolean
  bottomRight?: boolean
  topLeft?: boolean
  topRight?: boolean
}

export type InputProps = {
  additionalClassName?: string
  defaultValue?: string
  displayValidationMessage?: boolean
  id?: string
  label?: string
  name: string
  onBlur?: ChangeEventHandler<HTMLInputElement>
  onChange?: ChangeEventHandler<HTMLInputElement>
  placeholder?: string
  readOnly?: boolean
  required?: boolean
  roundedBorders?: RoundedBorders
  type?: InputType
  validation?: true | string
  disabled?: boolean
  hidden?: boolean
  theme?: 'dark' | 'light'
}

export const Input = forwardRef<HTMLInputElement, PropsWithChildren<InputProps>>(
  (
    {
      additionalClassName,
      children,
      defaultValue = '',
      displayValidationMessage = true,
      id,
      label,
      name,
      onChange: onChangeProps,
      required,
      roundedBorders,
      type,
      disabled = false,
      hidden = false,
      validation,
      theme = 'light',
      ...inputProps
    },
    ref
  ) => {
    const [value, setValue] = useState<string>(defaultValue)

    const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      e => {
        setValue(e.currentTarget.value)
        onChangeProps?.(e)
      },
      [onChangeProps]
    )

    let containerClassName = classNames(
      'flex w-full items-center font-t text-t-300 border transition-hover',
      // Setting overflow hidden to improve the looks of 1Password overlay
      'overflow-hidden',
      {
        // Using focus instead of focus-visible for Firefox as the :has pseudo-selector is not supported
        'focus-within:ring': isFirefox,
        '[&:has(:focus-visible)]:ring': !isFirefox,
        'rounded-15': !roundedBorders,
        'rounded-bl-15': roundedBorders?.bottomLeft,
        'rounded-br-15': roundedBorders?.bottomRight,
        'rounded-tl-15': roundedBorders?.topLeft,
        'rounded-tr-15': roundedBorders?.topRight,
        'opacity-0': hidden ?? false,
      },
      {
        'text-secondary-darker bg-neutral': theme === 'light',
        'text-white bg-transparent': theme === 'dark',
      }
    )

    if (validation === true && !isNullOrWhiteSpace(value)) {
      containerClassName = classNames(containerClassName, 'border-success', {
        'focus-within:ring-success/40': isFirefox,
        '[&:has(:focus-visible)]:ring-success/40': !isFirefox,
      })
    } else if (validation !== true && !isNullOrWhiteSpace(validation)) {
      containerClassName = classNames(containerClassName, 'border-secondary', {
        'focus-within:ring-error/40': isFirefox,
        '[&:has(:focus-visible)]:ring-error/40': !isFirefox,
      })
    } else {
      containerClassName = classNames(containerClassName, {
        'border-neutral-lighter-alt hover:border-secondary-darker':
          isNullOrWhiteSpace(value),
        'border-secondary-darker': !isNullOrWhiteSpace(value),
        'focus-within:border-secondary-darker focus-within:ring-primary/40': isFirefox,
        '[&:has(:focus-visible)]:border-secondary-darker [&:has(:focus-visible)]:ring-primary/40':
          !isFirefox,
      })
    }

    const inputId = !isNullOrWhiteSpace(id) ? id : name

    return (
      <div className={classNames('w-full', additionalClassName)}>
        {!isNullOrWhiteSpace(label) && (
          <Label additionalClassName="mb-10" htmlFor={inputId} required={required}>
            {label}
          </Label>
        )}

        <div className={containerClassName}>
          <input
            {...inputProps}
            autoComplete="on"
            className={classNames(
              'w-full p-15 placeholder-opacity-100 bg-transparent focus:outline-none',
              {
                'placeholder-neutral-dark-alt': theme === 'light',
                'placeholder-white': theme === 'dark',
              }
            )}
            defaultValue={defaultValue}
            id={inputId}
            name={name}
            onChange={onChange}
            ref={ref}
            disabled={disabled}
            required={required}
            hidden={hidden}
            type={type}
          />

          {children}
        </div>

        {displayValidationMessage && (
          <div className="min-h-15 mt-5 text-secondary font-t text-t-200 leading-[15px]">
            {validation !== true && !isNullOrWhiteSpace(validation) && validation}
          </div>
        )}
      </div>
    )
  }
)
